/**
 * Production-safe logger for the shared package.
 *
 * Works across web (Vite), mobile (Metro / React Native), and Node test
 * environments. In production builds, errors/warnings/info are suppressed
 * to avoid leaking tenant data, entity IDs, or stack traces to the browser
 * console. In development, messages pass through to the platform console.
 *
 * Consumers in shared code should prefer this over bare `console.*`.
 */

function isDev(): boolean {
  // React Native / Metro
  const rnDev = (globalThis as { __DEV__?: boolean }).__DEV__;
  if (typeof rnDev === "boolean") return rnDev;

  // Vite (web-supabase) — import.meta.env.DEV
  try {
    const meta = (import.meta as unknown as { env?: { DEV?: boolean } });
    const viteDev = meta?.env?.DEV;
    if (typeof viteDev === "boolean") return viteDev;
  } catch {
    // ignore
  }

  // Node / generic
  const nodeEnv = (globalThis as { process?: { env?: { NODE_ENV?: string } } })
    .process?.env?.NODE_ENV;
  if (nodeEnv) return nodeEnv !== "production";

  // Default to silent if we cannot determine the environment.
  return false;
}

export const logger = {
  error(message: string, ...args: unknown[]): void {
    if (isDev()) console.error(message, ...args);
  },
  warn(message: string, ...args: unknown[]): void {
    if (isDev()) console.warn(message, ...args);
  },
  info(message: string, ...args: unknown[]): void {
    if (isDev()) console.info(message, ...args);
  },
};
